import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseSheetHeightReturn {
  /** Live measured height of the observed sheet element, in pixels. */
  sheetHeightPx: number;
  /**
   * Ref callback to attach to the sheet element. Pass straight to
   * `BottomSheet`'s `onSheetMount` prop. Re-mounts wire up a fresh
   * observer; unmount tears it down and resets to 0.
   */
  onSheetMount: (node: HTMLDivElement | null) => void;
}

/**
 * Measures a bottom-sheet element with `ResizeObserver` so callers can
 * extend map bounds and offset focus pans above the obscured area.
 *
 * Observes the actual sheet (not the full-viewport wrapper) so the
 * reported height matches what's really covering the map. Seeds an
 * initial value from `getBoundingClientRect()` so consumers don't wait
 * for the first observer callback.
 */
export function useSheetHeight(): UseSheetHeightReturn {
  const [sheetHeightPx, setSheetHeightPx] = useState(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const onSheetMount = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!node || typeof ResizeObserver === 'undefined') {
      setSheetHeightPx(0);
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setSheetHeightPx(Math.round(entry.contentRect.height));
    });
    ro.observe(node);
    resizeObserverRef.current = ro;
    setSheetHeightPx(Math.round(node.getBoundingClientRect().height));
  }, []);

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  return { sheetHeightPx, onSheetMount };
}
